import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis
} from "@/components/ui/pagination";
import { Search, Loader2 } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { guides, saveGuideById, unsaveGuideById  } from "@/api/apiService";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import {
  fetchGuidesStart,
  fetchGuidesSuccess,
  fetchGuidesFailure,
  saveGuide,
  unsaveGuide,
  Guide,
  selectSavedGuideIds
} from "@/features/guides/guidesSlice";
import {flagUrl} from "@/helpers.ts";
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';


const Guides = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [isLiked, setIsLiked] = useState([]);
  const { items, loading, error, pagination } = useAppSelector(state => state.guides);
  const savedGuidesIds = useAppSelector(selectSavedGuideIds);

  const rollback = (id: number) =>
      setIsLiked(prev => prev.filter(_id => _id !== id));


  const handleSaveGuide = (guide: Guide) => () => {
    setIsLiked(prev => (prev.includes(guide.id) ? prev : [...prev, guide.id]));

    saveGuideById(guide.id)
        .then(() => {
          dispatch(saveGuide(guide));
        })
        .catch(err => {
          console.error("Failed to save guide:", err);
          rollback(guide.id);
        });
  };

  const handleUnsaveGuide = (guide: Guide) => () => {
    setIsLiked(prev => prev.filter(id => id !== guide.id));

    unsaveGuideById(guide.id)
        .then(() => {
          dispatch(unsaveGuide(guide));
        })
        .catch(err => {
          console.error("Failed to unsave guide:", err);
          setIsLiked(prev => (prev.includes(guide.id) ? prev : [...prev, guide.id]));
        });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchGuides = async () => {
      dispatch(fetchGuidesStart());
      try {
        const searchParam = debouncedSearchQuery ? `search=${encodeURIComponent(debouncedSearchQuery)}` : '';
        const response = await guides(page, true, searchParam);
        if (response && response.data) {
          dispatch(fetchGuidesSuccess({ data: response.data, meta: response.meta }));
          setIsLiked(savedGuidesIds || []);
        } else {
          dispatch(fetchGuidesSuccess({ data: [], meta: { current_page: page, from: 0, per_page: 15, to: 0, total: 0, total_pages: 0 } }));
        }
      } catch (error) {
        dispatch(fetchGuidesFailure(error instanceof Error ? error.message : "Failed to fetch guides"));
      }
    };

    fetchGuides().then();
  }, [dispatch, page, debouncedSearchQuery]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 mt-16">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Explore City Guides</h1>
            <p className="text-lg text-muted-foreground mb-8">
              Discover AI-powered travel guides for cities around the world. Find your next adventure with our curated recommendations.
            </p>
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search by city or country..."
                className="pl-10 w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="text-center text-destructive py-12">
              <p>{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.length > 0 ? (
                items.map((guide) => (
                  <Card
                    key={guide.id}
                    className="group cursor-pointer hover:shadow-lg transition-shadow relative"
                  >
                    {isLiked.includes(guide.id) ? (
                        <FavoriteIcon
                            onClick={handleUnsaveGuide(guide)}
                            className="absolute top-3 left-3 text-4xl w-12 h-12 flex items-center justify-center text-red-500 z-10"
                        />
                    ) : (
                        <FavoriteBorderIcon
                            onClick={handleSaveGuide(guide)}
                            className="absolute top-3 left-3 text-4xl w-12 h-12 flex items-center justify-center text-red-500 z-10"
                        />
                    )}
                    <div className="relative" onClick={() => navigate(`/guides/${guide.id}`)}>
                      <img
                          src={guide.image_url}
                          alt={`${guide.city}, ${guide.country}`}
                          className="w-full h-48 object-cover rounded-t-lg"
                      />
                      <img
                          className="absolute top-3 right-3 text-4xl bg-white/90 rounded-full w-12 h-12 flex items-center justify-center shadow-md"
                          src={flagUrl(guide.country)}
                          alt={`Flag of ${guide.country}`}
                      />
                    </div>
                    <CardHeader>
                      <h3 className="text-xl font-semibold whitespace-nowrap truncate max-w-[90%]">{guide.city}</h3>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-muted-foreground whitespace-nowrap truncate max-w-[60%]">{guide.country}</p>
                        <p className="text-sm text-muted-foreground whitespace-nowrap truncate max-w-[40%] text-right">by {guide.user_name}</p>
                      </div>
                    </CardHeader>
                  </Card>
                ))
              ) : (
                  <div className="col-span-full text-center py-12">
                  <p className="text-muted-foreground">No guides found matching your search.</p>
                </div>
              )}
            </div>
          )}

          {pagination && pagination.total_pages > 1 && !loading && (
            <div className="mt-8">
              <Pagination>
                <PaginationContent>
                  {pagination.current_page > 1 && (
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setPage(pagination.current_page - 1)}
                        className="cursor-pointer"
                      />
                    </PaginationItem>
                  )}

                  {pagination.current_page > 2 && (
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => setPage(1)}
                        className="cursor-pointer"
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  {pagination.current_page > 3 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}

                  {pagination.current_page > 1 && (
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => setPage(pagination.current_page - 1)}
                        className="cursor-pointer"
                      >
                        {pagination.current_page - 1}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  <PaginationItem>
                    <PaginationLink
                      isActive={true}
                      className="cursor-pointer"
                    >
                      {pagination.current_page}
                    </PaginationLink>
                  </PaginationItem>

                  {pagination.current_page < pagination.total_pages && (
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => setPage(pagination.current_page + 1)}
                        className="cursor-pointer"
                      >
                        {pagination.current_page + 1}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  {pagination.current_page < pagination.total_pages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}

                  {pagination.current_page < pagination.total_pages - 1 && (
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => setPage(pagination.total_pages)}
                        className="cursor-pointer"
                      >
                        {pagination.total_pages}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  {pagination.current_page < pagination.total_pages && (
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setPage(pagination.current_page + 1)}
                        className="cursor-pointer"
                      />
                    </PaginationItem>
                  )}
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Guides;
